// Time calculation utility based on elevation gradient and surface type

export interface TimeEstimate {
  hours: number
  minutes: number
  formatted: string // e.g., "4h 30m"
  totalMinutes: number
}

export interface SpeedRange {
  min: number
  max: number
  average: number
}

export interface DifficultyLevel {
  gradient: { min: number; max: number }
  difficulty: string
  pavedSpeed: SpeedRange
  unpavedSpeed: SpeedRange
}

// Speed and difficulty mapping based on elevation gradient (m/km)
const DIFFICULTY_LEVELS: DifficultyLevel[] = [
  {
    gradient: { min: 0, max: 10 },
    difficulty: 'Flat',
    pavedSpeed: { min: 25, max: 28, average: 26.5 },
    unpavedSpeed: { min: 20, max: 23, average: 21.5 }
  },
  {
    gradient: { min: 10, max: 20 },
    difficulty: 'Rolling',
    pavedSpeed: { min: 22, max: 25, average: 23.5 },
    unpavedSpeed: { min: 18, max: 21, average: 19.5 }
  },
  {
    gradient: { min: 20, max: 35 },
    difficulty: 'Hilly',
    pavedSpeed: { min: 20, max: 23, average: 21.5 },
    unpavedSpeed: { min: 16, max: 19, average: 17.5 }
  },
  {
    gradient: { min: 35, max: 50 },
    difficulty: 'Hard',
    pavedSpeed: { min: 17, max: 20, average: 18.5 },
    unpavedSpeed: { min: 13, max: 16, average: 14.5 }
  },
  {
    gradient: { min: 50, max: 75 },
    difficulty: 'Very Hard',
    pavedSpeed: { min: 14, max: 17, average: 15.5 },
    unpavedSpeed: { min: 11, max: 14, average: 12.5 }
  },
  {
    gradient: { min: 75, max: Infinity },
    difficulty: 'Brutal',
    pavedSpeed: { min: 12, max: 15, average: 13.5 },
    unpavedSpeed: { min: 9, max: 12, average: 10.5 }
  }
]

/**
 * Get difficulty level based on elevation gradient (m/km)
 */
export function getDifficultyLevel(elevationGradient: number): DifficultyLevel {
  return DIFFICULTY_LEVELS.find(level => 
    elevationGradient >= level.gradient.min && elevationGradient < level.gradient.max
  ) || DIFFICULTY_LEVELS[0]
}

/**
 * Calculate elevation gradient from elevation gain and distance
 */
export function calculateElevationGradient(elevationGain: number, distance: number): number {
  if (distance <= 0 || elevationGain <= 0) return 0
  return elevationGain / distance // m/km
}

/**
 * Format time from total minutes to readable format
 */
export function formatTime(totalMinutes: number): TimeEstimate {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = Math.round(totalMinutes % 60)
  
  let formatted = ''
  if (hours > 0) {
    formatted += `${hours}h`
    if (minutes > 0) {
      formatted += ` ${minutes}m`
    }
  } else {
    formatted = `${minutes}m`
  }
  
  return {
    hours,
    minutes,
    formatted,
    totalMinutes: Math.round(totalMinutes)
  }
}

/**
 * Calculate estimated time based on distance, elevation gain, and surface percentage
 */
export function calculateEstimatedTime(
  distance: number, // km
  elevationGain: number, // meters
  unpavedPercentage: number = 0, // 0-100
  speedType: 'min' | 'max' | 'average' = 'average'
): {
  estimate: TimeEstimate
  range: { min: TimeEstimate; max: TimeEstimate }
  difficulty: string
  gradient: number
} {
  // Calculate elevation gradient
  const gradient = calculateElevationGradient(elevationGain, distance)
  const difficultyLevel = getDifficultyLevel(gradient)
  
  // Calculate weighted speed based on surface mix
  const pavedPercentage = 100 - unpavedPercentage
  const pavedSpeed = difficultyLevel.pavedSpeed[speedType]
  const unpavedSpeed = difficultyLevel.unpavedSpeed[speedType]
  
  // Weighted average speed
  const averageSpeed = (
    (pavedSpeed * pavedPercentage / 100) + 
    (unpavedSpeed * unpavedPercentage / 100)
  )
  
  // Calculate time in minutes
  const timeInMinutes = (distance / averageSpeed) * 60
  
  // Calculate range (min and max times)
  const minSpeed = (
    (difficultyLevel.pavedSpeed.max * pavedPercentage / 100) + 
    (difficultyLevel.unpavedSpeed.max * unpavedPercentage / 100)
  )
  const maxSpeed = (
    (difficultyLevel.pavedSpeed.min * pavedPercentage / 100) + 
    (difficultyLevel.unpavedSpeed.min * unpavedPercentage / 100)
  )
  
  const minTime = (distance / minSpeed) * 60
  const maxTime = (distance / maxSpeed) * 60
  
  return {
    estimate: formatTime(timeInMinutes),
    range: {
      min: formatTime(minTime),
      max: formatTime(maxTime)
    },
    difficulty: difficultyLevel.difficulty,
    gradient: Math.round(gradient * 10) / 10 // Round to 1 decimal
  }
}

/**
 * Get speed range for a given difficulty level and surface type
 */
export function getSpeedRange(
  elevationGradient: number, 
  isPaved: boolean = true
): SpeedRange {
  const difficultyLevel = getDifficultyLevel(elevationGradient)
  return isPaved ? difficultyLevel.pavedSpeed : difficultyLevel.unpavedSpeed
}

/**
 * Calculate estimated time for multiple surface segments
 */
export function calculateSegmentedTime(segments: Array<{
  distance: number // km
  elevationGain: number // meters
  isPaved: boolean
}>): {
  totalTime: TimeEstimate
  segments: Array<{
    time: TimeEstimate
    difficulty: string
    gradient: number
  }>
} {
  let totalMinutes = 0
  const segmentResults = segments.map(segment => {
    const result = calculateEstimatedTime(
      segment.distance,
      segment.elevationGain,
      segment.isPaved ? 0 : 100
    )
    totalMinutes += result.estimate.totalMinutes
    return {
      time: result.estimate,
      difficulty: result.difficulty,
      gradient: result.gradient
    }
  })
  
  return {
    totalTime: formatTime(totalMinutes),
    segments: segmentResults
  }
}

/**
 * Simple helper for quick time estimation
 */
export function quickTimeEstimate(
  distance: number,
  elevationGain: number,
  unpavedPercentage: number = 0,
  routeType?: string
): string {
  let result = calculateEstimatedTime(distance, elevationGain, unpavedPercentage)
  
  // Apply route type adjustments
  if (routeType === 'Rail Trail') {
    // Rail trails are about 15% slower due to road crossings, gates, and other obstacles
    const adjustedMinutes = Math.round(result.estimate.totalMinutes * 1.15)
    result.estimate = formatTime(adjustedMinutes)
  }
  
  return result.estimate.formatted
}